import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { baseUrlsService } from '../../../components/api/baseUrlsService';
import { loadOmniChatCatalogs, lookupGateway } from './omniChatCatalogs';
import { omniChatApi } from './omniChatApi';
import { coercePolicies } from './omniChatConstants';
import { logOmniChat } from './omniChatLogger';
import type { OmniChatAgent, OmniChatFormState, OmniChatGateway, OmniChatInferenceKey } from './omniChatTypes';

type OmniChatStoreValue = {
  ready: boolean;
  catalogsReady: boolean;
  catalogsFailedAll: boolean;
  agents: OmniChatAgent[];
  gateways: OmniChatGateway[];
  inferenceKeys: OmniChatInferenceKey[];
  assistantUrl: string;
  getAgent: (id: string) => OmniChatAgent | undefined;
  findGateway: (id: string) => OmniChatGateway | undefined;
  saveAgent: (form: OmniChatFormState, id?: string) => Promise<OmniChatAgent>;
  setActive: (id: string, active: boolean) => Promise<OmniChatAgent>;
  removeAgent: (id: string) => Promise<void>;
};

const OmniChatContext = createContext<OmniChatStoreValue | null>(null);

function hydrateAgent(agent: OmniChatAgent): OmniChatAgent {
  return {
    ...agent,
    connectorIds: [...(agent.connectorIds ?? [])],
    knowledgeBaseIds: [...(agent.knowledgeBaseIds ?? [])],
    policies: coercePolicies(agent.policies),
    active: agent.active !== false,
    createdBy: agent.createdBy || '',
    createdByName: agent.createdByName || '',
    createdByEmail: agent.createdByEmail || '',
    createdAt: agent.createdAt || '',
    inferenceQuotaId: agent.inferenceQuotaId || '',
    inferenceKeyLast4: agent.inferenceKeyLast4 || '',
  };
}

export function OmniChatProvider({ children }: { children: React.ReactNode }) {
  const [agents, setAgents] = useState<OmniChatAgent[]>([]);
  const [ready, setReady] = useState(false);
  const [gateways, setGateways] = useState<OmniChatGateway[]>([]);
  const [inferenceKeys, setInferenceKeys] = useState<OmniChatInferenceKey[]>([]);
  const [assistantUrl, setAssistantUrl] = useState('');
  const [catalogsReady, setCatalogsReady] = useState(false);
  const [catalogsFailedAll, setCatalogsFailedAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void omniChatApi
      .list()
      .then((items) => {
        if (cancelled) return;
        setAgents(items.map(hydrateAgent));
        setReady(true);
        logOmniChat('app', 'store_hydrate', { count: items.length });
      })
      .catch((error) => {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'unreadable';
        logOmniChat('error', 'store_hydrate_failed', { message });
        setAgents([]);
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void baseUrlsService
      .get()
      .then((urls) => {
        if (cancelled) return;
        setAssistantUrl(urls.omniChatAssistantUrl);
        logOmniChat('app', 'base_url_hydrate', { hasUrl: Boolean(urls.omniChatAssistantUrl) });
      })
      .catch((error) => {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'unreadable';
        logOmniChat('error', 'base_url_hydrate_failed', { message });
        setAssistantUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void loadOmniChatCatalogs().then(({ catalogs, failed }) => {
      if (cancelled) return;
      setGateways(catalogs.gateways);
      setInferenceKeys(catalogs.inferenceKeys);
      setCatalogsFailedAll(failed);
      setCatalogsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const getAgent = useCallback((id: string) => {
    const agent = agents.find((item) => item.id === id);
    return agent ? hydrateAgent(agent) : undefined;
  }, [agents]);

  const findGateway = useCallback((id: string) => lookupGateway(id, gateways), [gateways]);

  const saveAgent = useCallback(async (form: OmniChatFormState, id?: string) => {
    const title = form.title.trim();
    const instruction = form.instruction.trim();
    if (!title || !instruction) {
      logOmniChat('warning', 'agent_save_invalid', { hasTitle: Boolean(title), hasInstruction: Boolean(instruction) });
      throw new Error('Title and agent instruction are required.');
    }

    if (id) {
      const existing = agents.find((agent) => agent.id === id);
      if (!existing) {
        logOmniChat('error', 'agent_save_missing', { id });
        throw new Error('This agent is no longer available.');
      }
      const saved = hydrateAgent(
        await omniChatApi.update(id, {
          ...form,
          connectorIds: existing.connectorIds ?? [],
          knowledgeBaseIds: existing.knowledgeBaseIds ?? [],
        })
      );
      setAgents((prev) => prev.map((agent) => (agent.id === id ? saved : agent)));
      logOmniChat('app', 'agent_updated', { id, title: saved.title });
      logOmniChat('app', 'review_policies_save', { id, policies: saved.policies });
      return saved;
    }

    const saved = hydrateAgent(await omniChatApi.create({ ...form, connectorIds: [], knowledgeBaseIds: [] }));
    setAgents((prev) => [saved, ...prev.filter((agent) => agent.id !== saved.id)]);
    logOmniChat('app', 'agent_created', { id: saved.id, title: saved.title });
    return saved;
  }, [agents]);

  const setActive = useCallback(async (id: string, active: boolean) => {
    const saved = hydrateAgent(await omniChatApi.setActive(id, active));
    setAgents((prev) => prev.map((agent) => (agent.id === id ? saved : agent)));
    logOmniChat('app', active ? 'agent_activated' : 'agent_deactivated', { id, title: saved.title });
    return saved;
  }, []);

  const removeAgent = useCallback(async (id: string) => {
    await omniChatApi.remove(id);
    setAgents((prev) => prev.filter((agent) => agent.id !== id));
    logOmniChat('warning', 'agent_deleted', { id });
  }, []);

  const value = useMemo(
    () => ({
      ready,
      catalogsReady,
      catalogsFailedAll,
      agents,
      gateways,
      inferenceKeys,
      assistantUrl,
      getAgent,
      findGateway,
      saveAgent,
      setActive,
      removeAgent,
    }),
    [agents, assistantUrl, catalogsFailedAll, catalogsReady, findGateway, gateways, getAgent, inferenceKeys, ready, removeAgent, saveAgent, setActive]
  );

  return <OmniChatContext.Provider value={value}>{children}</OmniChatContext.Provider>;
}

export function useOmniChat(): OmniChatStoreValue {
  const context = useContext(OmniChatContext);
  if (!context) throw new Error('useOmniChat must be used within OmniChatProvider');
  return context;
}
